import { apiClient } from './client'
import type { ItineraryDay } from './tourOperators'
import type { MeasurementUnit } from '../measurements'
import type { HikeDifficulty, HikeDifficultyCalculation, HikeDifficultyMode, WireHikeDifficulty } from '../hikeDifficulty'
import type { PlanGenerationProfile } from '../planGenerationProfile'

const ADMIN_POPULAR_HIKES_BASE = '/api/admin/popular-hikes'

export type PopularHike = {
  id: string
  operatorId?: string | null
  operatorName?: string | null
  tripName: string
  description?: string | null
  linkToItinerary?: string | null
  linkToMap?: string | null
  unGeoscheme?: string | null
  country?: string | null
  stateOrProvince?: string | null
  geographicRegion?: string | null
  city?: string | null
  protectedArea?: string | null
  tripExternalId?: string | null
  difficulty?: string | null
  difficultyLevel?: WireHikeDifficulty | null
  difficultyMode?: HikeDifficultyMode | null
  difficultyCalculation?: HikeDifficultyCalculation | null
  planGenerationProfile?: PlanGenerationProfile | null
  packWeight?: string | null
  highestPoint?: string | null
  aboveEightThousandFeet?: boolean | null
  tripNumberOfDays?: number | null
  multipleDailyOptions?: boolean | null
  totalElevationGainMeters?: number | null
  totalElevationLossMeters?: number | null
  totalDistanceKm?: number | null
  /** Whole-trip distance for LongItinerary hikes, whose itinerary only itemizes the first days. */
  totalTripDistanceKm?: number | null
  /** Whole-trip elevation gain for LongItinerary hikes. */
  totalTripElevationGainMeters?: number | null
  sourceMeasurementUnit?: MeasurementUnit | null
  isFavorite: boolean
  thumbnailUrl?: string | null
  heroImageUrls: string[]
  itinerary: ItineraryDay[]
}

export type PopularHikePayload = {
  operatorId?: string | null
  operatorName?: string | null
  tripName: string
  description?: string | null
  linkToItinerary?: string | null
  linkToMap?: string | null
  unGeoscheme?: string | null
  country?: string | null
  stateOrProvince?: string | null
  geographicRegion?: string | null
  city?: string | null
  protectedArea?: string | null
  tripExternalId?: string | null
  difficulty?: string | null
  difficultyLevel?: HikeDifficulty | null
  difficultyMode: HikeDifficultyMode
  planGenerationProfile: PlanGenerationProfile
  packWeight?: string | null
  highestPoint?: string | null
  aboveEightThousandFeet?: boolean | null
  tripNumberOfDays?: number | null
  multipleDailyOptions?: boolean | null
  totalElevationGainMeters?: number | null
  totalElevationLossMeters?: number | null
  totalDistanceKm?: number | null
  /** Whole-trip distance for LongItinerary hikes, whose itinerary only itemizes the first days. */
  totalTripDistanceKm?: number | null
  /** Whole-trip elevation gain for LongItinerary hikes. */
  totalTripElevationGainMeters?: number | null
  sourceMeasurementUnit?: MeasurementUnit | null
  isFavorite: boolean
  itinerary: ItineraryDay[]
}

export type GenerateImageUploadUrlPayload = {
  fileName: string
  contentType: string
  kind: 'hero' | 'thumbnail'
}

export type GenerateImageUploadUrlResponse = {
  uploadUrl: string
  publicUrl: string
  objectKey: string
}

export async function generatePopularHikeImageUploadUrl(
  id: string,
  payload: GenerateImageUploadUrlPayload
) {
  const { data } = await apiClient.post<GenerateImageUploadUrlResponse>(
    `${ADMIN_POPULAR_HIKES_BASE}/${id}/images/upload-url`,
    payload
  )
  return data
}

export async function addPopularHikeHeroImage(id: string, imageUrl: string) {
  const { data } = await apiClient.post<PopularHike>(`${ADMIN_POPULAR_HIKES_BASE}/${id}/hero-images`, imageUrl, {
    headers: { 'Content-Type': 'application/json' }
  })
  return data
}

export async function uploadPopularHikeHeroImage(id: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await apiClient.post<PopularHike>(`${ADMIN_POPULAR_HIKES_BASE}/${id}/hero-images/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return data
}

export async function deletePopularHikeHeroImage(id: string, imageUrl: string) {
  const { data } = await apiClient.delete<PopularHike>(`${ADMIN_POPULAR_HIKES_BASE}/${id}/hero-images`, {
    data: imageUrl,
    headers: { 'Content-Type': 'application/json' }
  })
  return data
}

export async function getPopularHikes() {
  const { data } = await apiClient.get<PopularHike[]>(ADMIN_POPULAR_HIKES_BASE)
  return data
}

export type ResetAllPopularHikesResponse = {
  deletedCount: number
}

export async function resetAllPopularHikes() {
  const { data } = await apiClient.post<ResetAllPopularHikesResponse>(`${ADMIN_POPULAR_HIKES_BASE}/debug/reset-all`)
  return data
}

export async function createPopularHike(payload: PopularHikePayload) {
  const { data } = await apiClient.post<PopularHike>(ADMIN_POPULAR_HIKES_BASE, payload)
  return data
}

export async function updatePopularHike(id: string, payload: PopularHikePayload) {
  const { data } = await apiClient.put<PopularHike>(`${ADMIN_POPULAR_HIKES_BASE}/${id}`, payload)
  return data
}

export async function deletePopularHike(id: string) {
  await apiClient.delete(`${ADMIN_POPULAR_HIKES_BASE}/${id}`)
}

export type RegenerateThumbnailsResponse = {
  processed: number
  skipped: number
  failed: number
  errors: string[]
}

export async function regeneratePopularHikeThumbnails(id: string) {
  const { data } = await apiClient.post<RegenerateThumbnailsResponse>(
    `${ADMIN_POPULAR_HIKES_BASE}/${id}/hero-images/regenerate-thumbnails`
  )
  return data
}

export async function regenerateAllPopularHikeThumbnails() {
  const { data } = await apiClient.post<RegenerateThumbnailsResponse>(
    `${ADMIN_POPULAR_HIKES_BASE}/regenerate-all-thumbnails`
  )
  return data
}
